/**
 * Keyframe Capture Routing
 *
 * There is no separate keyframe "edit mode". Outside play mode, if an object
 * lives under a Keyframes container with a selected (applied) keyframe, edits
 * to that object are stored in the selected keyframe instead of authored
 * SceneObjectData. Selecting the Base state (no keyframe applied) restores
 * normal authored editing.
 *
 * Spec: docs-internal/KEYFRAME_EDITOR_STATES.md
 *
 * Capture surfaces:
 * - Gizmo drags → captureTransformForKeyframe (called by undo-gizmo-service)
 * - Active toggles → captureActiveForKeyframe (called by index.ts / panel)
 * - Property commits → always-on BEHAVIOR_CHANGED listener, wired once by
 *   initKeyframePropertyCapture()
 */

import type { Keyframes, KeyframeEntry, PropertyValue, PropertyDeclaration } from 'trace-viewer/behaviors';
import { Properties } from 'trace-viewer/behaviors';
import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { getUndoManager, BehaviorCommitCommand, liveLocalPose } from 'trace-viewer/editor';
import { dispatchBehaviorChanged, EDITOR_EVENTS, type BehaviorChangedDetail } from './editor-events';

export interface KeyframeCaptureContext {
    /** The container SceneObject that owns the Keyframes behavior */
    container: SceneObject;
    /** The Keyframes behavior instance */
    behavior: Keyframes;
    /** The selected keyframe edits are routed into */
    keyframeName: string;
}

/**
 * Resolve where edits to `obj` should be stored: the nearest container
 * (self-or-ancestor) whose Keyframes behavior has a selected keyframe.
 * Returns null at Base (no keyframe applied anywhere up the chain) and
 * during play mode — in both cases edits go to authored data as usual.
 */
export function getKeyframeCaptureContext(obj: SceneObject): KeyframeCaptureContext | null {
    if (obj.traceScene.isPlayMode()) return null;
    let current: SceneObject | null = obj;
    while (current) {
        const kf = current.getBehavior('Keyframes') as Keyframes | null;
        const active = kf?.getActiveKeyframe();
        if (kf && active) {
            return { container: current, behavior: kf, keyframeName: active };
        }
        current = current.getParent();
    }
    return null;
}

/**
 * Entry key for `obj` inside `container`'s keyframe entries. The container
 * itself is always keyed `'./'` (never `'$self'`, which getRelativePath
 * returns) so each object has one canonical entry key.
 */
function entryPath(obj: SceneObject, container: SceneObject): string | null {
    if (obj === container) return './';
    return container.traceScene.getRelativePath(obj, container) || null;
}

/**
 * Build a sparse `entries` map keying the LIVE local pose of the container and
 * every descendant that can receive a keyframe contribution (i.e. has a
 * Properties behavior — the runtime skips entries on bare nodes). Used when a new
 * keyframe is added so it snapshots the current ("active") look of the subtree
 * instead of starting empty. Objects without Properties are omitted: their pose
 * can't be contributed, so keying them would be a dead entry.
 */
export function captureSubtreePoseEntries(container: SceneObject): Record<string, KeyframeEntry> {
    const entries: Record<string, KeyframeEntry> = {};
    const key = (obj: SceneObject) => {
        if (!obj.getBehavior(Properties)) return;
        const path = entryPath(obj, container);
        if (!path) return;
        entries[path] = { transform: liveLocalPose(obj) };
    };
    key(container);
    for (const d of container.getDescendants()) key(d);
    return entries;
}

/**
 * The active keyframe's stored values for `obj`, or null when there's no capture
 * context (Base / play mode) or the keyframe has no entry for this object. Reads
 * the authored keyframe entry directly — NOT `Properties.getValue`, which returns
 * the mid-transition interpolated value while a keyframe animation is in flight.
 * The authoring panel must snap to the keyframe's target value, not an in-between
 * frame, so this is deliberately transition-agnostic.
 */
function getActiveKeyframeValues(obj: SceneObject): Record<string, PropertyValue> | null {
    const ctx = getKeyframeCaptureContext(obj);
    if (!ctx) return null;
    const { container, behavior, keyframeName } = ctx;
    const path = entryPath(obj, container);
    if (!path) return null;
    return behavior.getData().keyframes[keyframeName]?.entries[path]?.values ?? null;
}

/**
 * The value the property editor should DISPLAY for `propertyName` when a keyframe
 * is selected: the active keyframe's stored override, or `undefined` if the
 * keyframe doesn't override it (caller falls back to the authored Base
 * declaration). Snaps to the keyframe's target value — see
 * `getActiveKeyframeValues`.
 */
export function getKeyframeDisplayValue(obj: SceneObject, propertyName: string): PropertyValue | undefined {
    return getActiveKeyframeValues(obj)?.[propertyName];
}

/**
 * Overlay the active keyframe's stored property values onto a Properties data
 * snapshot, returning a new object with each overridden declaration's `value`
 * replaced by the keyframe's. Used to SEED the panel's editable state when a
 * keyframe is selected, so the value fields are driven by a single source
 * (the seeded `localData`) that updates synchronously as the user types —
 * rather than racing a separate per-render resolver against the async keyframe
 * write (which masked in-progress typing with the stale stored value).
 *
 * Returns `data` unchanged at Base / play mode, or when the property type isn't
 * `Properties`. Never mutates the input.
 */
export function overlayKeyframeValues<T extends { '@type': string; properties?: PropertyDeclaration[] }>(
    obj: SceneObject,
    data: T,
): T {
    if (data['@type'] !== 'Properties' || !data.properties) return data;
    const values = getActiveKeyframeValues(obj);
    if (!values) return data;
    return {
        ...data,
        properties: data.properties.map(decl =>
            decl.name in values ? { ...decl, value: values[decl.name] } : decl,
        ),
    };
}

/**
 * Capture an active (visibility) change into the selected keyframe.
 * Returns true if the capture was handled (caller should skip normal
 * setActive + undo push).
 */
export function captureActiveForKeyframe(obj: SceneObject, active: boolean): boolean {
    const ctx = getKeyframeCaptureContext(obj);
    if (!ctx) return false;
    const { container, behavior, keyframeName } = ctx;

    const path = entryPath(obj, container);
    if (!path) return false;

    const data = behavior.getData();
    const keyframe = data.keyframes[keyframeName];
    if (!keyframe) return false;

    const before = behavior.getData();

    const existing = keyframe.entries[path];
    keyframe.entries[path] = {
        ...existing,
        active,
    };

    // Same Properties auto-attach as captureTransformForKeyframe — every
    // keyframe entry is pushed as a state contribution, and contributions
    // require Properties on the target.
    void ensureProperties(obj).then(({ added }) => behavior.commit(data).then(() => ({ added }))).then(({ added }) => {
        behavior.reapply();

        if (!getUndoManager().isUndoRedoing) {
            getUndoManager().push(new BehaviorCommitCommand(
                container,
                'Keyframes',
                before,
                behavior.getData(),
            ));
        }

        dispatchBehaviorChanged({
            objectPath: container.getPath(),
            behaviorType: 'Keyframes',
            action: 'updated',
        });

        if (added) {
            dispatchBehaviorChanged({
                objectPath: obj.getPath(),
                behaviorType: 'Properties',
                action: 'added',
            });
        }
    });

    return true;
}

/**
 * Capture a transform change into the selected keyframe as a delta over the
 * object's authored pose. Called from the undo-gizmo-service after a gizmo
 * drag completes. Returns true if the capture was handled (caller should
 * skip normal undo push).
 *
 * KeyframeEntry.transform is a `TransformOverride` (delta), matching
 * `StateDefinition.transform`. The delta is computed against the authored
 * pose stored in SceneObjectData.transform (which is the user's "rest"
 * pose, independent of any selected keyframe).
 */
export function captureTransformForKeyframe(obj: SceneObject): boolean {
    const ctx = getKeyframeCaptureContext(obj);
    if (!ctx) return false;
    const { container, behavior, keyframeName } = ctx;

    const path = entryPath(obj, container);
    if (!path) return false;

    // ABSOLUTE local pose (see ABSOLUTE_STATE_TRANSFORMS.md) — store the live
    // local transform directly; the keyframe replaces the transform wholesale.
    // Same primitive as the state capture (no delta math).
    const transform: KeyframeEntry['transform'] = liveLocalPose(obj);

    // Update the keyframe data
    const data = behavior.getData();
    const keyframe = data.keyframes[keyframeName];
    if (!keyframe) return false;

    const before = behavior.getData();

    // Merge transform into existing entry or create new one
    const existing = keyframe.entries[path];
    keyframe.entries[path] = {
        ...existing,
        transform,
    };

    // Keyframe contributions push onto the target's Properties resolver — auto-
    // attach an empty Properties behavior if the target doesn't have one, so the
    // delta actually reapplies on navigation. Without this the runtime would
    // skip the entry with a console warning, and the user would see their drag
    // "disappear" the next time they navigate keyframes.
    void ensureProperties(obj).then(({ added }) => behavior.commit(data).then(() => ({ added }))).then(({ added }) => {
        // Re-navigate to apply the new keyframe state. reapply() pushes the
        // contribution synchronously, which triggers _ensureSavedTransform on
        // the target's Properties — populating the authored cache that
        // captureTransform consults. Only AFTER this is the panel refresh safe
        // to dispatch; otherwise the panel's loadBehaviors → refreshSceneData
        // would call captureTransform before _savedTransform exists and would
        // stomp authored with the live (post-drag) pose.
        behavior.reapply();

        // Push undo
        if (!getUndoManager().isUndoRedoing) {
            getUndoManager().push(new BehaviorCommitCommand(
                container,
                'Keyframes',
                before,
                behavior.getData(),
            ));
        }

        dispatchBehaviorChanged({
            objectPath: container.getPath(),
            behaviorType: 'Keyframes',
            action: 'updated',
        });

        // If we just implicitly attached Properties, tell the panel to refresh
        // so the new behavior shows up. Do this *after* reapply so _savedTransform
        // is populated by the time the panel's refreshSceneData runs.
        if (added) {
            dispatchBehaviorChanged({
                objectPath: obj.getPath(),
                behaviorType: 'Properties',
                action: 'added',
            });
        }
    });

    return true;
}

// ── Property commit capture (always-on listener) ──

let _getTraceScene: (() => TraceScene | null) | null = null;

/**
 * Last-seen declaration values per object, updated on every Properties
 * commit event (including at Base and during undo). Captures diff against
 * this baseline so only the values the user actually CHANGED land in the
 * keyframe entry — snapshotting every declared value would freeze unrelated
 * properties at stale values, breaking base-inheritance ("a keyframe is its
 * diff from Base"). When no baseline exists yet (first-ever commit on an
 * object), we degrade to capturing all declared values.
 */
const _declBaselines = new WeakMap<SceneObject, Map<string, PropertyValue>>();

/**
 * Wire the always-on listener that routes Properties commits into the
 * selected keyframe. Called once at sandbox startup (next to the overlay
 * mounts). Returns a dispose function.
 *
 * In tests, pass a closure over the test helper's traceScene.
 */
export function initKeyframePropertyCapture(getTraceScene: () => TraceScene | null): () => void {
    _getTraceScene = getTraceScene;
    window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, _onBehaviorChanged);
    return () => {
        window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, _onBehaviorChanged);
        _getTraceScene = null;
    };
}

/**
 * Route a just-committed Properties edit on `obj` into the selected keyframe,
 * reverting the authored (Base) declarations the panel wrote so the keyframe
 * entry becomes the SOLE carrier of the edit.
 *
 * Returns the `Keyframes` undo command for the keyframe-entry write, or `null`
 * if nothing was captured (Base, no usable path, no changed values, mid
 * undo/redo). The caller decides what to do with it:
 *  - the panel pushes it as the ONE undo entry for the edit (its own Properties
 *    command is dropped, since the Base write is reverted to a no-op here);
 *  - the always-on listener pushes it directly (legacy dispatch path).
 *
 * This keeps the Base write + revert + keyframe write inside a single logical
 * action with one undo entry — undoing removes the keyframe override and leaves
 * Base untouched, and redo re-adds it without re-leaking into Base (the old
 * two-ungrouped-commands shape let redo replay the Base write; see
 * keyframe-property-undo.test.ts).
 */
export async function capturePropertyEditIntoKeyframe(obj: SceneObject): Promise<BehaviorCommitCommand | null> {
    // Use the declaration's authored value rather than `getValue()`. The
    // resolver's getValue resolution order puts active contributions above
    // the local declaration, so when this Keyframes is currently contributing
    // a value (from a prior capture / navigation), getValue would echo the
    // contribution back into the entry — silently dropping the user's edit.
    // The declaration value is what the user just authored in the inspector.
    const props = obj.getBehavior(Properties);
    if (!props) return null;

    // Refresh the declaration baseline on EVERY commit (also at Base and
    // during undo) so the next captured commit can diff against it.
    const current = new Map<string, PropertyValue>();
    for (const decl of props.getProperties()) {
        if (decl.value !== undefined) current.set(decl.name, decl.value);
    }
    const baseline = _declBaselines.get(obj);
    _declBaselines.set(obj, current);

    // Undo/redo replays Properties commits; re-capturing them would corrupt
    // the keyframe entry being restored.
    if (getUndoManager().isUndoRedoing) return null;

    const ctx = getKeyframeCaptureContext(obj);
    if (!ctx) return null;

    const { container, behavior, keyframeName } = ctx;
    const path = entryPath(obj, container);
    if (!path) return null;

    // Capture only the values that changed since the last commit; with no
    // baseline (first-ever commit on this object), capture all declared.
    const values: Record<string, PropertyValue> = {};
    for (const [key, value] of current) {
        if (!baseline || JSON.stringify(baseline.get(key)) !== JSON.stringify(value)) {
            values[key] = value;
        }
    }
    if (Object.keys(values).length === 0) return null;

    // Write into the keyframe entry
    const data = behavior.getData();
    const keyframe = data.keyframes[keyframeName];
    if (!keyframe) return null;

    const before = behavior.getData();
    const existing = keyframe.entries[path];
    keyframe.entries[path] = {
        ...existing,
        values: {
            ...(existing?.values ?? {}),
            ...values,
        },
    };

    await behavior.commit(data);

    // Close the "dual-write" gap (KEYFRAME_EDITOR_STATES.md): the edit was
    // committed into the authored declaration BEFORE we got here — so Base
    // changed too, and every keyframe without its own override inherited the
    // edit. Restore the authored declarations to their baseline values; the
    // keyframe entry written above is now the only carrier of the edit.
    if (baseline) {
        const revertedDecls = props.getData().properties.map(decl =>
            decl.name in values && baseline.has(decl.name)
                ? { ...decl, value: baseline.get(decl.name) } as typeof decl
                : decl,
        );
        await props.commit({ properties: revertedDecls });
        // Re-baseline to the restored values so the NEXT edit diffs against
        // Base, not against the value we just reverted.
        const restored = new Map(current);
        for (const name of Object.keys(values)) {
            const base = baseline.get(name);
            if (base !== undefined) restored.set(name, base);
        }
        _declBaselines.set(obj, restored);
    }

    // Re-push the keyframe contribution LAST — the Properties revert commit
    // above resets the resolver, so the contribution must be re-applied for the
    // viewport to keep showing the keyframe's value.
    behavior.reapply();

    // Re-checked here (not just at entry): an undo could have begun while the
    // async commits were in flight.
    if (getUndoManager().isUndoRedoing) return null;

    return new BehaviorCommitCommand(
        container,
        'Keyframes',
        before,
        behavior.getData(),
    );
}

/**
 * Legacy dispatch path: any code that commits Properties and dispatches a
 * BEHAVIOR_CHANGED 'updated' event (e.g. AI tools) gets routed into the
 * selected keyframe here. The behaviors panel does NOT rely on this — it calls
 * capturePropertyEditIntoKeyframe directly so the keyframe write and its undo
 * entry are part of the panel's atomic edit (see liveCommit).
 */
function _onBehaviorChanged(event: Event): void {
    const detail = (event as CustomEvent<BehaviorChangedDetail>).detail;
    // 'added' too: the first bound-value edit on an object with no Properties
    // attaches one and dispatches 'added'. Ignoring it let that first edit
    // leak into Base while a keyframe was selected (trace-viewer#701 review).
    if (detail.behaviorType !== 'Properties' || (detail.action !== 'updated' && detail.action !== 'added')) return;

    const traceScene = _getTraceScene?.();
    if (!traceScene) return;
    const obj = traceScene.resolveSceneObject(detail.objectPath);
    if (!obj) return;

    void capturePropertyEditIntoKeyframe(obj).then(command => {
        if (command && !getUndoManager().isUndoRedoing) {
            getUndoManager().push(command);
        }
        // Don't dispatch BEHAVIOR_CHANGED here — would cause infinite loop
    });
}

/**
 * Add an empty Properties behavior to `obj` if it doesn't already have one.
 * Returns `{ added }` so the caller can sequence a BEHAVIOR_CHANGED dispatch
 * AFTER the rest of the capture chain (commit + reapply) — otherwise the
 * panel-side `loadBehaviors → refreshSceneData → captureTransform` would
 * run before the keyframe contribution had a chance to populate
 * Properties' `_savedTransform`, and would stomp authored with the live
 * (post-drag) pose, producing a visible jump on the next interaction.
 */
async function ensureProperties(obj: SceneObject): Promise<{ added: boolean }> {
    if (obj.getBehavior(Properties)) return { added: false };
    await obj.addBehavior(Properties, { properties: [], states: {} });
    return { added: true };
}
